(() => {
  if (customElements.get('bachkami-size-guide')) return;

  class BachkamiSizeGuide extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.charts = JSON.parse(this.querySelector('[data-charts]').textContent);
      this.images = JSON.parse(this.querySelector('[data-images]').textContent);
      this.state = {
        type: 'jacket',
        fit: 'jacket',
        length: 'R',
        number: 40,
        unit: 'in',
        compare: false
      };
      this.mount = this.querySelector('[data-mount]');
      this.draw();
    }

    el(tag, text, cls) {
      const e = document.createElement(tag);
      if (text !== undefined) e.textContent = text;
      if (cls) e.className = cls;
      return e;
    }

    button(text, active, action) {
      const b = this.el('button', text);
      b.type = 'button';
      b.setAttribute('aria-pressed', String(active));
      b.onclick = action;
      return b;
    }

    format(value) {
      if (value == null) return '—';
      return Number(
        (this.state.unit === 'cm' ? value : value / 2.54)
          .toFixed(this.state.unit === 'cm' ? 1 : 2)
      ).toString();
    }

    async openChat(event, status) {
      event.preventDefault();

      if (location.protocol === 'file:') {
        status.textContent = 'Chat is available after this guide is installed on your store.';
        return;
      }

      const chat = document.querySelector('shopify-chat');

      if (chat) {
        try {
          await Promise.race([
            customElements.whenDefined('shopify-chat'),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Chat loading')), 4000)
            )
          ]);

          if (typeof chat.show === 'function') {
            await chat.show();
            return;
          }

          if ('open' in chat) {
            chat.open = true;
            return;
          }
        } catch {}
      }

      const legacy = document.querySelector('shopify-inbox')
        ?.shadowRoot?.querySelector('button');

      if (legacy) {
        legacy.click();
        return;
      }

      window.location.assign('/pages/contact');
    }

    get chart() {
      return this.charts[this.state.fit];
    }

    numbers() {
      return this.state.type === 'jacket'
        ? Array.from({ length: 10 }, (_, i) => 36 + 2 * i)
        : Array.from({ length: 15 }, (_, i) => 26 + 2 * i);
    }

    draw(focusKey) {
      const s = this.state;
      const c = this.chart;

      this.mount.replaceChildren();

      const wrap = this.el(
        'div',
        undefined,
        'cs-wrap' + (s.compare ? ' cs-comparing' : '')
      );

      this.mount.append(wrap);

      const hero = this.el('div', undefined, 'cs-hero');
      hero.append(
        this.el('p', 'SAMIR BACHKAMI', 'cs-kicker'),
        this.el('h1', 'Size Chart'),
        this.el(
          'p',
          'Find your size. Explore our silhouettes and compare the measurements with a garment you already love.'
        )
      );
      wrap.append(hero);

      const types = this.el('div', undefined, 'cs-type');
      types.setAttribute('role', 'group');
      types.setAttribute('aria-label', 'Garment type');

      for (const [type, label] of [
        ['jacket', 'Jackets'],
        ['trousers', 'Trousers']
      ]) {
        const b = this.button(label, s.type === type, () => {
          s.type = type;
          s.fit = type === 'jacket' ? 'jacket' : 'regular';
          s.number = type === 'jacket' ? 40 : 32;
          this.draw('type-' + type);
        });
        b.dataset.focus = 'type-' + type;
        types.append(b);
      }

      wrap.append(types);

      const fits = this.el('div', undefined, 'cs-fit');
      fits.setAttribute('role', 'group');
      fits.setAttribute('aria-label', 'Garment fit');

      const choices = s.type === 'trousers'
        ? [
            ['trousers', 'Slim'],
            ['regular', 'Regular'],
            ['wide', 'Wide-Leg']
          ]
        : [
            ['jacket', 'Double Breasted Jacket'],
            ['sandviken', 'Single Breasted Peak Lapel'],
            ['lomma', 'Single Breasted Notch Lapel']
          ];

      for (const [key, label] of choices) {
        const b = this.button(label, s.fit === key, () => {
          s.fit = key;
          this.draw('fit-' + key);
        });

        if (s.type === 'jacket') {
          b.dataset.short = key === 'jacket'
            ? 'Double Breasted'
            : key === 'sandviken'
              ? 'SB Peak Lapel'
              : 'SB Notch Lapel';
        }

        b.dataset.focus = 'fit-' + key;
        fits.append(b);
      }

      wrap.append(fits);

      const heading = this.el('div', undefined, 'cs-heading');
      const title = this.el('div');
      title.append(
        this.el('h2', c.name),
        this.el('p', 'Finished garment measurements · full circumferences')
      );
      heading.append(title);
      wrap.append(heading);

      const controls = this.el('div', undefined, 'cs-controls');
      const left = this.el('div', undefined, 'cs-control-group');
      const right = this.el('div', undefined, 'cs-control-group');

      const lengths = this.el('div', undefined, 'cs-toggle');
      lengths.setAttribute('role', 'group');
      lengths.setAttribute('aria-label', 'Garment length');

      for (const [code, name] of [
        ['S', 'Short'],
        ['R', 'Regular'],
        ['L', 'Long']
      ]) {
        const b = this.button(name, s.length === code, () => {
          s.length = code;
          this.draw('length-' + code);
        });
        b.dataset.focus = 'length-' + code;
        lengths.append(b);
      }

      const lab = this.el(
        'label',
        s.type === 'jacket' ? 'Jacket size · US' : 'Trouser waist size · US'
      );
      const select = this.el('select');
      select.dataset.focus = 'size';

      for (const n of this.numbers()) {
        const o = this.el(
          'option',
          n + s.length + (c.sizes[n + s.length] ? '' : ' · unavailable')
        );
        o.value = n;
        select.append(o);
      }

      select.value = s.number;
      select.onchange = () => {
        s.number = Number(select.value);
        this.draw('size');
      };

      lab.append(select);
      left.append(lengths, lab);

      const units = this.el('div', undefined, 'cs-toggle');
      units.setAttribute('role', 'group');
      units.setAttribute('aria-label', 'Measurement units');

      for (const u of ['in', 'cm']) {
        const b = this.button(u.toUpperCase(), s.unit === u, () => {
          s.unit = u;
          this.draw('unit-' + u);
        });
        b.dataset.focus = 'unit-' + u;
        units.append(b);
      }

      const compare = this.button(
        s.compare ? 'View selected size' : 'Compare all sizes',
        s.compare,
        () => {
          s.compare = !s.compare;
          this.draw('compare');
        }
      );
      compare.className = 'cs-action';
      compare.dataset.focus = 'compare';

      right.append(units, compare);
      controls.append(left, right);
      wrap.append(controls);

      const record = c.sizes[s.number + s.length];

      if (s.compare) {
        const scroller = this.el('div', undefined, 'cs-table-wrap');
        scroller.tabIndex = 0;
        scroller.setAttribute(
          'aria-label',
          'All sizes. Scroll horizontally for more sizes.'
        );

        const table = this.el('table', undefined, 'cs-table');
        table.append(
          this.el(
            'caption',
            c.name + ' · ' + s.length + ' · ' +
              (s.unit === 'cm' ? 'centimetres' : 'inches')
          )
        );

        const head = this.el('thead');
        const tr = this.el('tr');
        const first = this.el('th', 'Measurement');
        first.scope = 'col';
        tr.append(first);

        for (const n of this.numbers()) {
          const th = this.el(
            'th',
            n + s.length,
            n === s.number ? 'cs-selected' : undefined
          );
          th.scope = 'col';
          tr.append(th);
        }

        head.append(tr);
        table.append(head);

        const body = this.el('tbody');

        for (const [key, name] of c.metrics) {
          const row = this.el('tr');
          const th = this.el('th', name);
          th.scope = 'row';
          row.append(th);

          for (const n of this.numbers()) {
            row.append(
              this.el(
                'td',
                this.format(c.sizes[n + s.length]?.[key]),
                n === s.number ? 'cs-selected' : undefined
              )
            );
          }

          body.append(row);
        }

        table.append(body);
        scroller.append(table);
        wrap.append(
          scroller,
          this.el(
            'p',
            'Scroll across to see every size. A dash means the supplied chart has no measurement for that size.',
            'cs-note cs-scroll-note'
          )
        );
      } else {
        const view = this.el('div', undefined, 'cs-view');
        const figure = this.el('figure', undefined, 'cs-figure');

        figure.innerHTML = s.type === 'jacket'
          ? this.jacketChartDiagram()
          : this.trouserChartDiagram();

        if (s.type === 'jacket' && s.fit === 'lomma') {
          const options = this.el('div', undefined, 'cs-photo-options');

          for (const [alt, label] of [
            [false, 'View 1'],
            [true, 'View 2']
          ]) {
            options.append(
              this.button(label, !!s.alternate === alt, () => {
                s.alternate = alt;
                this.draw();
              })
            );
          }

          figure.append(options);
        }

        figure.append(
          this.el(
            'figcaption',
            'Measurement directions shown on a reference garment. Silhouette is illustrative. Dashed arrows refer to the back of the garment.'
          )
        );

        const details = this.el('div');
        details.append(
          this.el('div', s.number + s.length, 'cs-size'),
          this.el(
            'p',
            'FINISHED GARMENT · ' +
              (s.unit === 'cm' ? 'CENTIMETRES' : 'INCHES'),
            'cs-caption'
          )
        );

        if (record) {
          const metrics = this.el('dl', undefined, 'cs-metrics');

          const letters = s.type === 'jacket'
            ? {
                chest: 'A', waist: 'B', hips: 'C',
                shoulder: 'D', sleeve: 'E', length: 'F', cuff: 'G'
              }
            : {
                waist: 'A', hips: 'B', thigh: 'C',
                lowerLeg: 'D', opening: 'E', inseam: 'F',
                outseam: 'G', frontRise: 'H', backRise: 'I'
              };

          for (const [key, name] of c.metrics) {
            const row = this.el('div');
            const dd = this.el('dd', this.format(record[key]));

            if (record[key] != null) {
              dd.append(this.el('small', ' ' + s.unit));
            }

            row.append(
              this.el(
                'dt',
                (letters[key] ? letters[key] + ' · ' : '') + name
              ),
              dd
            );
            metrics.append(row);
          }

          details.append(metrics);
        } else {
          details.append(
            this.el(
              'p',
              'Measurements for this size have not been supplied. Please contact us before choosing a size; we have not estimated the missing values.',
              'cs-empty'
            )
          );
        }

        view.append(figure, details);
        wrap.append(view);
      }

      if (c.source_note && s.fit !== 'wide') {
        wrap.append(this.el('p', c.source_note, 'cs-note'));
      }

      const help = this.el('details', undefined, 'cs-help');
      help.append(
        this.el('summary', 'How to compare your measurements'),
        this.el(
          'p',
          'Lay a similar garment that fits you well flat, with the fabric relaxed and buttons fastened. Measure across the garment, then double flat chest, waist, hip, leg and hem widths to compare with the full circumferences shown here. Do not double shoulder width, sleeve length, garment length or rises. These measurements describe the finished garment, not your body.'
        ),
        this.el(
          'p',
          s.type === 'trousers'
            ? 'Measure the upper leg 15 cm below the crotch. Measure the lower leg ' +
              (record?.lower_leg_reference_cm || (s.length === 'L' ? 34 : 32)) +
              ' cm below the crotch. Inside leg runs from the crotch seam to the hem. For back rise, turn the trousers over.'
            : 'Measure sleeve length from the shoulder seam to the cuff. Turn the jacket over to measure its centre-back length. Short, Regular and Long refer to garment length; they do not describe the fit silhouette.'
        )
      );
      wrap.append(help);

      const assistance = this.el('section', undefined, 'cs-assistance');
      const helpCopy = this.el('div');
      helpCopy.append(
        this.el('p', 'HERE TO HELP', 'cs-kicker'),
        this.el('h2', 'Find your perfect fit.'),
        this.el(
          'p',
          'Need help choosing your size or comparing measurements? Our team can guide you through the options.'
        )
      );

      const tailor = this.el('div');
      tailor.append(
        this.el('h3', 'Made to measure'),
        this.el(
          'p',
          'We offer made-to-measure for all our suits and trousers. Chat with us about your measurements and preferred fit.'
        )
      );

      const contact = this.el('a', 'Chat with us', 'cs-chat');
      contact.href = '/pages/contact';

      const chatStatus = this.el('p', undefined, 'cs-note');
      chatStatus.setAttribute('role', 'status');
      contact.onclick = e => this.openChat(e, chatStatus);

      tailor.append(contact, chatStatus);
      assistance.append(helpCopy, tailor);
      wrap.append(assistance);

      const live = this.el(
        'div',
        c.name + ', size ' + s.number + s.length + ', ' + s.unit,
        'cs-status'
      );
      live.setAttribute('role', 'status');
      wrap.append(live);

      if (focusKey) {
        this.mount.querySelector('[data-focus="' + focusKey + '"]')
          ?.focus({ preventScroll: true });
      }
    }

    measurementDiagram(image, lines, trousers) {
      const id = trousers ? 'chartTrouserArrow' : 'chartJacketArrow';

      const escapedImage = String(image || '').replace(
        /[&<>"']/g,
        character => ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;'
        })[character]
      );

      return `
        <svg viewBox="0 0 1430 ${trousers ? 1450 : 1440}"
             role="img"
             aria-label="${trousers ? 'Trouser' : 'Jacket'} measurement locations">
          <defs>
            <marker id="${id}" viewBox="0 0 12 12"
                    refX="6" refY="6" markerWidth="5" markerHeight="5"
                    orient="auto-start-reverse">
              <path d="M1 1 11 6 1 11Z"
                    fill="#322a22" stroke="#fff" stroke-width=".6"/>
            </marker>
          </defs>
          <image href="${escapedImage}" x="200" y="80"
                 width="1000" height="1295"
                 preserveAspectRatio="xMidYMid meet"/>
          ${lines.map(([key, label, coords, labelCoords]) => {
            const [x1, y1, x2, y2] = coords;
            const [tx, ty, lx, ly] = labelCoords;
            const back = trousers ? key === 'I' : key === 'F';
            const width = trousers ? 290 : key === 'D' ? 235 : 190;
            const height = trousers ? (back ? 82 : 57) : 72;

            return `
              <g data-chart-measure="${key}">
                <line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}"
                      stroke="#fff" stroke-width="11" opacity=".95"/>
                <line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}"
                      stroke="#322a22" stroke-width="5"
                      ${back ? 'stroke-dasharray="14 9"' : ''}
                      marker-start="url(#${id})"
                      marker-end="url(#${id})"/>
                <path d="M${tx + (trousers ? 80 : 60)} ${ty + 12} L${lx} ${ly}"
                      stroke="#776d61" stroke-width="2" fill="none"/>
                <rect x="${tx - 8}" y="${ty - (trousers ? 29 : 28)}"
                      width="${width}" height="${height}" rx="6"
                      fill="#fafaf8" fill-opacity=".98"/>
                <text x="${tx}" y="${ty}"
                      font-family="Montserrat,Arial,sans-serif"
                      font-size="${trousers ? 27 : 28}" font-weight="600"
                      fill="#25221f">${key} · ${label}</text>
                ${back ? `
                  <text x="${tx}" y="${ty + (trousers ? 30 : 29)}"
                        font-family="Montserrat,Arial,sans-serif"
                        font-size="20" fill="#777">Measure on back</text>
                ` : ''}
              </g>
            `;
          }).join('')}
        </svg>
      `;
    }

    jacketChartDiagram() {
      const lines = [
        ['A', 'Chest', [465,615,955,615], [45,570,460,615]],
        ['B', 'Waist', [455,835,965,835], [45,815,450,835]],
        ['C', 'Hips', [455,1240,965,1240], [45,1280,450,1240]],
        ['D', 'Shoulders', [380,295,1010,295], [500,65,700,290]],
        ['E', 'Sleeve', [1012,300,1065,1140], [1210,590,1033,640]],
        ['F', 'Back length', [1160,210,1160,1300], [1210,1090,1160,1000]],
        ['G', 'Cuff', [290,1150,400,1150], [45,1080,285,1150]]
      ];

      const image = this.state.fit === 'lomma' && this.state.alternate
        ? this.images.notchAlternate
        : this.images[this.state.fit] || this.images.jacket;

      return this.measurementDiagram(image, lines, false);
    }

    trouserChartDiagram() {
      const fit = this.state.fit === 'trousers' ? 'slim' : this.state.fit;
      const crotch = fit === 'wide' ? 540 : fit === 'regular' ? 475 : 465;
      const outer = fit === 'regular' ? 480 : fit === 'wide' ? 490 : 510;
      const hem = fit === 'slim' ? 520 : fit === 'regular' ? 485 : 470;

      const lines = [
        ['A', 'Waist', [500,150,900,150], [35,110,500,169]],
        ['B', 'Hips', [outer,385,930,385], [35,350,482,385]],
        ['C', 'Upper leg', [outer,630,695,630], [35,620,485,615]],
        ['D', 'Lower leg', [hem,920,690,920], [35,865,497,885]],
        ['E', 'Hem opening', [hem,1310,680,1310], [35,1300,514,1298]],
        ['F', 'Inside leg', [700,crotch,680,1315], [1100,850,680,900]],
        ['G', 'Outside leg', [965,180,965,1295], [1100,1110,965,1110]],
        ['H', 'Front rise', [700,150,700,crotch], [1100,240,700,300]],
        ['I', 'Back rise', [1030,180,1030,575], [1100,490,1030,490]]
      ];

      return this.measurementDiagram(
        this.images[fit] || this.images.trousers,
        lines,
        true
      );
    }
  }

  customElements.define('bachkami-size-guide', BachkamiSizeGuide);
})();